import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@frontend/shadcn/components/ui/dialog';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@frontend/shadcn/components/ui/select';
import { Group, Project } from '../filters-form/types';

interface AssignPostsDialogProps {
  projects: Project[];
  groups: Group[];
  selectedCount: number;
  onAssign: (campaign: string, projectName: string, group: string) => void;
  children?: React.ReactNode;
}

const AssignPostsDialog: React.FC<AssignPostsDialogProps> = ({
  projects,
  groups,
  selectedCount,
  onAssign,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<{ name: string; id: string }>({
    name: '',
    id: '',
  });
  const [selectedGroup, setSelectedGroup] = useState('');

  const handleAssign = () => {
    if (!selectedCampaign.id && !selectedGroup) {
      return; // Don't proceed if no selections are made
    }
    onAssign(selectedCampaign.id, selectedCampaign.name, selectedGroup);
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="bg-white max-w-md">
        <DialogHeader className="border-b pb-2">
          <DialogTitle>Assign Posts</DialogTitle>
        </DialogHeader>

        <div className="px-6">
          <p className="text-sm text-gray-600 mb-4">
            Assign {selectedCount > 0 ? selectedCount : '[n]'} posts to a project and/or a group.
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Assign Project</label>
              <Select
                value={selectedCampaign.id?.toString() ?? ''}
                onValueChange={(value) => {
                  const project = projects.find((p) => p.id.toString() === value);
                  if (project) {
                    setSelectedCampaign({ id: project.id.toString(), name: project.name });
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={project.id.toString()}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Assign Group</label>
              <Select value={selectedGroup} onValueChange={setSelectedGroup}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a group" />
                </SelectTrigger>
                <SelectContent>
                  {groups.map((group) => (
                    <SelectItem key={group.id} value={group.id.toString()}>
                      {group.name}
                    </SelectItem>
                ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <DialogFooter className="border-t flex sm:justify-between gap-2">
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleAssign}
            disabled={!selectedCampaign.id && !selectedGroup}
          >
            Assign {selectedCount > 0 ? selectedCount : '[n]'} Posts
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AssignPostsDialog;
